import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { submitKnowledge } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/lib/i18n";

export default function KnowledgeStep({ attempt, onAdvance }) {
  const { t } = useT();
  const concept = attempt.current_concept;
  const questions = concept.knowledge.questions;
  const [answers, setAnswers] = useState(Array(questions.length).fill(-1));
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (answers.some((a) => a < 0)) {
      toast.error(t("step.knowledge_answer_all"));
      return;
    }
    setBusy(true);
    try {
      await submitKnowledge(attempt.id, { concept_id: concept.id, answers });
      await onAdvance();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-12 pb-24" data-testid="knowledge-step">
      <div className="paper p-6 md:p-8">
        <div className="label-mono">{t("step.knowledge_layer")}</div>
        <h2 className="font-display text-2xl font-bold mt-1">{t("step.knowledge_title")}</h2>
        <p className="text-sm text-[var(--iddl-muted)] mt-2">{t("step.knowledge_sub")}</p>

        <div className="mt-6 grid gap-6">
          {questions.map((q, qi) => (
            <div key={qi} className="border-t border-[var(--iddl-rule)] pt-5" data-testid={`question-${qi}`}>
              <div className="label-mono">{t("step.knowledge_q", { n: qi + 1 })}</div>
              <p className="font-display text-base font-semibold mt-1">{q.q}</p>
              <div className="mt-3 grid gap-2">
                {q.options.map((opt, oi) => (
                  <label
                    key={oi}
                    className={`flex items-start gap-3 p-3 border cursor-pointer ${answers[qi] === oi ? "border-[var(--iddl-ink)] bg-[var(--iddl-surface)]" : "border-[var(--iddl-rule)] hover:bg-[var(--iddl-surface)]"}`}
                    data-testid={`option-${qi}-${oi}`}
                  >
                    <input
                      type="radio"
                      name={`q-${qi}`}
                      checked={answers[qi] === oi}
                      onChange={() => setAnswers(answers.map((a, k) => (k === qi ? oi : a)))}
                      className="mt-1"
                    />
                    <span className="text-sm">{opt}</span>
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <Button onClick={submit} disabled={busy} data-testid="btn-submit-knowledge" className="btn-ink rounded-none h-11 px-6">
            {busy ? t("step.knowledge_submitting") : t("step.knowledge_submit")}
            <ArrowRight size={14} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
